import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSandboxSession } from '../../contexts/SandboxSessionContext';
import { showToast } from '../../utils/toast';
import { useSwipeToClose } from '../../hooks/useSwipeToClose';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useClickOutside } from '../../hooks/useClickOutside';
import ConfirmModal from '../Modal/ConfirmModal';
import './OrganizationSwitcher.css';

interface OrganizationSwitcherProps {
  isCollapsed?: boolean;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ isCollapsed = false }) => {
  const { availableOrganizations, currentOrganization, switchOrganization } = useAuth();
  const sandbox = useSandboxSession();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  // AB3 Academy (Lane C.4): если активна practice — спрашиваем подтверждение
  // перед сменой org, потому что sandbox привязан к (user_id, org_id) и
  // переключение организации убивает прогресс модуля.
  const [pendingSwitchOrg, setPendingSwitchOrg] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile(640);
  const swipeHandleRef = useSwipeToClose<HTMLSpanElement>({
    enabled: isMobile && isOpen,
    onClose: () => setIsOpen(false),
  });

  // Виджет нужен только когда есть из чего выбирать.
  const visible = availableOrganizations.length > 1;

  // Click-outside / Escape — закрытие dropdown. На мобильных backdrop сам закрывает,
  // но useClickOutside даёт бонусом Escape без отдельного listener'а.
  useClickOutside(rootRef, () => setIsOpen(false), isOpen && !isMobile);

  if (!visible) return null;

  const performSwitch = async (orgId: number) => {
    setPendingId(orgId);
    try {
      // switchOrganization внутри делает window.location.reload()
      // — после успеха управление сюда не вернётся.
      await switchOrganization(orgId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось переключить организацию';
      showToast(message, 'error', 2500);
      setPendingId(null);
      setIsOpen(false);
    }
  };

  const handleSwitch = async (orgId: number) => {
    if (pendingId !== null) return;
    if (orgId === currentOrganization?.id) {
      setIsOpen(false);
      return;
    }
    // AB3 Academy: блокируем смену org пока юзер в practice — потеряем sandbox.
    if (sandbox.isActive()) {
      setPendingSwitchOrg(orgId);
      return;
    }
    await performSwitch(orgId);
  };

  const handleSandboxAwareConfirm = async () => {
    const orgId = pendingSwitchOrg;
    if (orgId === null) return;
    sandbox.finish('manual');
    setPendingSwitchOrg(null);
    await performSwitch(orgId);
  };

  const handleSandboxAwareCancel = () => {
    setPendingSwitchOrg(null);
  };

  const triggerLabel = currentOrganization?.code || currentOrganization?.name || 'Организация';

  return (
    <div className="org-switcher" ref={rootRef}>
      <button
        type="button"
        className={`org-switcher-trigger${isOpen ? ' org-switcher-trigger--open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={currentOrganization?.name || triggerLabel}
        disabled={pendingId !== null}
      >
        <Building2 size={14} />
        {!isCollapsed && (
          <>
            <span className="org-switcher-trigger-label">{triggerLabel}</span>
            <ChevronDown
              size={14}
              style={{
                transition: 'transform 0.15s ease',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
              }}
            />
          </>
        )}
      </button>

      {isOpen && (() => {
        // На мобильном bottom-sheet рендерим через portal в document.body,
        // чтобы containing block родителей (sidebar с transform) не сжимал ширину.
        const content = (
          <>
            <div
              className="org-switcher-backdrop"
              aria-hidden
              onClick={() => setIsOpen(false)}
            />
            <div className="org-switcher-dropdown" role="listbox" aria-label="Доступные организации">
              <span className="org-switcher-handle" aria-hidden ref={swipeHandleRef} />
              <div className="org-switcher-dropdown-title">Выберите организацию</div>
              {availableOrganizations.map((org) => {
                const isCurrent = org.id === currentOrganization?.id;
                const isPending = pendingId === org.id;
                return (
                  <button
                    key={org.id}
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    className={`org-switcher-item${isCurrent ? ' org-switcher-item--current' : ''}`}
                    onClick={() => handleSwitch(org.id)}
                    disabled={pendingId !== null}
                  >
                    <div className="org-switcher-item-icon">
                      {isPending ? (
                        <span className="spinner spinner-sm" />
                      ) : isCurrent ? (
                        <Check size={14} />
                      ) : (
                        <Building2 size={14} />
                      )}
                    </div>
                    <div className="org-switcher-item-content">
                      <div className="org-switcher-item-name">
                        {org.name || org.code || `#${org.id}`}
                      </div>
                      {org.code && org.name && (
                        <div className="org-switcher-item-code">{org.code}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        );
        return isMobile ? createPortal(content, document.body) : content;
      })()}

      <ConfirmModal
        isOpen={pendingSwitchOrg !== null}
        onClose={handleSandboxAwareCancel}
        onConfirm={handleSandboxAwareConfirm}
        title="Выйти из режима практики?"
        message="Сейчас активна практика AB3 Academy. Переключение организации закроет песочницу, и прогресс по этому модулю станет несданным."
        confirmText="Сменить организацию"
        cancelText="Остаться"
        isDestructive
      />
    </div>
  );
};

export default OrganizationSwitcher;
